
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/services/profiles";
import { toast } from "sonner";

// Type for statistics
export interface Statistics {
  total_users: number;
  total_reagents: number;
  total_swaps_completed: number;
  total_credits_exchanged: number;
  total_messages_sent: number;
  total_swap_requests: number;
  total_reagent_swaps: number;
}

export const fetchAllProfiles = async (): Promise<Profile[]> => {
  try {
    console.log("Fetching all profiles for admin panel");
    
    // Get all profiles without joining with users table
    const { data: profiles, error: profilesError } = await supabase
      .from("profiles")
      .select("*")
      .order("created_at", { ascending: false })
      .eq("is_deleted", false);

    if (profilesError) {
      console.error("Error fetching profiles:", profilesError);
      toast.error("Failed to load user profiles");
      throw new Error(profilesError.message);
    }

    console.log(`Successfully fetched ${profiles.length} profiles`);
    return profiles as Profile[];
  } catch (error) {
    console.error("Exception fetching profiles:", error);
    toast.error("An unexpected error occurred");
    throw error;
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<Profile>): Promise<Profile | null> => {
  try {
    console.log("Updating user profile:", userId, updates);
    
    const { data, error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", userId)
      .select("*")
      .single();

    if (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update user profile");
      throw new Error(error.message);
    }

    console.log("Profile updated successfully:", data);
    return data as Profile;
  } catch (error) {
    console.error("Exception updating profile:", error);
    toast.error("An unexpected error occurred");
    throw error;
  }
};

export const fetchStatistics = async (): Promise<Statistics> => {
  try {
    console.log("Fetching system statistics");
    
    // Use the new stored procedure for getting statistics
    const { data, error } = await supabase.rpc('get_admin_statistics');
    
    if (error) {
      console.error("Error fetching statistics from function:", error);
      // Fall back to the statistics table if the RPC fails
      const { data: basicStats, error: basicStatsError } = await supabase
        .from("statistics")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(1)
        .single();

      if (basicStatsError) {
        console.error("Error fetching basic statistics:", basicStatsError);
        toast.error("Failed to load statistics");
        return getDefaultStatistics();
      }

      return {
        total_users: basicStats.total_users,
        total_reagents: basicStats.total_reagents,
        total_swaps_completed: basicStats.total_swaps_completed,
        total_credits_exchanged: basicStats.total_credits_exchanged,
        total_messages_sent: 0,
        total_swap_requests: 0,
        total_reagent_swaps: 0
      };
    }

    console.log("Statistics fetched successfully:", data[0]);

    // The RPC returns an array with a single object
    return {
      total_users: data[0].total_users,
      total_reagents: data[0].total_reagents,
      total_swaps_completed: data[0].total_swaps_completed,
      total_credits_exchanged: data[0].total_credits_exchanged,
      total_messages_sent: data[0].total_messages,
      total_swap_requests: data[0].total_swap_requests,
      total_reagent_swaps: data[0].total_reagent_swaps
    };
  } catch (error) {
    console.error("Exception fetching statistics:", error);
    return getDefaultStatistics();
  }
};

// Helper function to return default statistics on error
const getDefaultStatistics = (): Statistics => {
  return {
    total_users: 0,
    total_reagents: 0,
    total_swaps_completed: 0,
    total_credits_exchanged: 0,
    total_messages_sent: 0,
    total_swap_requests: 0,
    total_reagent_swaps: 0
  };
};
